import React, { useMemo, useState, useCallback } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import type { Locale } from 'date-fns';

export interface TimeSeriesPoint {
  date: string; // yyyy-MM-dd
  values: Record<string, number>;
}

interface TimeSeriesChartProps {
  data: TimeSeriesPoint[];
  previousData?: TimeSeriesPoint[];
  metricKey: string;
  color?: string;
  previousColor?: string;
  dimmed?: boolean; // visualize as blurred/dimmed when all zeros
  overlayText?: string;
  showLegend?: boolean;
  currentLegend?: string;
  previousLegend?: string;
  currentTooltipLabel?: string;
  previousTooltipLabel?: string;
  dateLocale?: Locale;
  // New: multi-series support for Summary tab (one line per platform)
  multiSeries?: Record<string, string[]>; // { platformId: ['metricKey1','metricKey2'] } - if provided, metricKey is a single metric and we render one series per platform listed in keys
  platformColors?: Record<string, string>; // platformId -> color
}

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({ data, previousData, metricKey, color = '#6366f1', previousColor = '#a5b4fc', dimmed = false, overlayText, showLegend = true, currentLegend, previousLegend, currentTooltipLabel, previousTooltipLabel, dateLocale, multiSeries, platformColors }) => {
  const platformIds = useMemo(() => multiSeries ? Object.keys(multiSeries) : [], [multiSeries]);
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});
  const toggleSeries = useCallback((id: string) => {
    setHiddenSeries((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);
  const baseData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((point) => ({
      date: point.date,
      value: Number(point?.values?.[metricKey] ?? 0),
    }));
  }, [data, metricKey]);

  const basePreviousData = useMemo(() => {
    if (!Array.isArray(previousData)) return [];
    return previousData.map((point) => ({
      date: point.date,
      value: Number(point?.values?.[metricKey] ?? 0),
    }));
  }, [previousData, metricKey]);

  const syntheticData = useMemo(() => {
    const dates: string[] = baseData.length > 0
      ? baseData.map(d => d.date)
      : Array.from({ length: 30 }).map((_, i) => {
          const d = new Date();
          d.setDate(d.getDate() - (29 - i));
          return format(d, 'yyyy-MM-dd');
        });
    const pattern = [2, 8, 20, 35, 12, 5, 1, 0, 6, 22, 40, 18, 3, 1, 15, 28, 6, 2, 12, 4, 1, 0, 10, 2, 0, 8, 1, 0, 14, 3];
    return dates.map((date, i) => ({ date, value: pattern[i % pattern.length] }));
  }, [baseData]);

  const isAllZero = useMemo(() => baseData.length === 0 || baseData.every(p => (p.value || 0) === 0), [baseData]);

  // Build combined chart data aligning previous series to current dates
  const chartData = useMemo(() => {
    // Multi-series mode: expect data as array of points where values contain per-platform keys already; we map to flat object per date with keys per platform
    if (multiSeries && platformIds.length > 0) {
      return (Array.isArray(data) ? data : []).map((point) => {
        const row: Record<string, number | string> = { date: point.date };
        for (const platformId of platformIds) {
          // For multi-series, we read from point.values[platformId]
          row[platformId] = Number((point as any)?.values?.[platformId] ?? 0);
        }
        return row;
      });
    }
    const currentSeries = (dimmed && isAllZero) ? syntheticData : baseData;
    if (!currentSeries.length) return [] as Array<{ date: string; current?: number; previous?: number }>;
    if (!basePreviousData.length) {
      return currentSeries.map(d => ({ date: d.date, current: d.value }));
    }
    // Align previous values across current date indices using proportional indexing
    const lenC = currentSeries.length;
    const lenP = basePreviousData.length;
    const aligned = currentSeries.map((d, i) => {
      const idxP = lenC > 1 ? Math.round((i * (lenP - 1)) / (lenC - 1)) : 0;
      const prevVal = basePreviousData[idxP]?.value ?? 0;
      return { date: d.date, current: d.value, previous: prevVal };
    });
    return aligned;
  }, [data, baseData, basePreviousData, syntheticData, dimmed, isAllZero, multiSeries]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!(active && payload && payload.length)) return null;
    const date = label ? format(parseISO(label), 'P', { locale: dateLocale }) : '';
    // Multi-series tooltip: list each series line with its value
    if (multiSeries && Object.keys(multiSeries).length > 0) {
      const total = (payload || []).reduce((sum: number, entry: any) => sum + Number(entry?.value ?? 0), 0);
      return (
        <div className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm text-sm">
          <div className="text-gray-500">{date}</div>
          <div className="mt-1 space-y-0.5">
            {payload.map((entry: any) => (
              <div key={`tip-${entry.dataKey}`} className="flex items-center gap-2 text-gray-900">
                <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: entry.stroke || '#6366f1' }} />
                <span className="capitalize">{entry.dataKey}</span>
                <span className="ml-auto font-medium">{Number(entry.value ?? 0).toLocaleString()}</span>
              </div>
            ))}
            <div className="h-px bg-gray-200 my-1" />
            <div className="flex items-center gap-2 text-gray-900">
              <span className="inline-block w-2 h-2 rounded-sm bg-gray-800" />
              <span className="font-medium">Total</span>
              <span className="ml-auto font-semibold">{total.toLocaleString()}</span>
            </div>
          </div>
        </div>
      );
    }
    const currentEntry = payload.find((p: any) => p.dataKey === 'current');
    const previousEntry = payload.find((p: any) => p.dataKey === 'previous');
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm text-sm">
        <div className="text-gray-500">{date}</div>
        {currentEntry && (
          <div className="font-medium text-gray-900">
            <span className="inline-block w-2 h-2 rounded-sm mr-2" style={{ backgroundColor: color }} />
            {Number(currentEntry.value ?? 0).toLocaleString()} {currentTooltipLabel ? <span className="text-gray-500">({currentTooltipLabel})</span> : null}
          </div>
        )}
        {previousEntry && (
          <div className="text-gray-700">
            <span className="inline-block w-2 h-2 rounded-sm mr-2" style={{ backgroundColor: previousColor }} />
            {Number(previousEntry.value ?? 0).toLocaleString()} {previousTooltipLabel ? <span className="text-gray-500">({previousTooltipLabel})</span> : null}
          </div>
        )}
      </div>
    );
  };

  if (!chartData.length) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 text-sm">
        No time series data
      </div>
    );
  }

  return (
    <div>
      <div className={`h-80 relative ${dimmed ? 'opacity-60 blur-[2px]' : ''}`}>
        <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="timeseriesColorCurrent" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="timeseriesColorPrevious" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={previousColor} stopOpacity={0.15} />
              <stop offset="95%" stopColor={previousColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => {
              try {
                return format(parseISO(value), 'd MMM', { locale: dateLocale });
              } catch {
                return value;
              }
            }}
          />
          <YAxis stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
          <Tooltip content={<CustomTooltip />} />
          {multiSeries && platformIds.length > 0 ? (
            // Render one series per platform in multi-series mode
            <>
              {platformIds.map((platformId) => (
                <Area
                  key={`series-${platformId}`}
                  type="monotone"
                  dataKey={platformId}
                  stroke={platformColors?.[platformId] || '#6366f1'}
                  strokeWidth={2}
                  fillOpacity={0.2}
                  fill={platformColors?.[platformId] || '#6366f1'}
                  dot={false}
                  isAnimationActive={true}
                  animationDuration={400}
                  stackId="platforms"
                  hide={!!hiddenSeries[platformId]}
                />
              ))}
            </>
          ) : (
            <>
              {chartData.some((d: any) => typeof d.previous === 'number') && (
                <Area
                  type="monotone"
                  dataKey="previous"
                  stroke={previousColor}
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  fillOpacity={0.2}
                  fill="url(#timeseriesColorPrevious)"
                  dot={false}
                  isAnimationActive={false}
                />
              )}
              <Area
                type="monotone"
                dataKey="current"
                stroke={color}
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#timeseriesColorCurrent)"
                dot={false}
              />
            </>
          )}
        </AreaChart>
        </ResponsiveContainer>
        {dimmed && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-black/40 text-white px-4 py-2 rounded-lg border border-white/40 text-sm">
              {overlayText || 'No activity for this period'}
            </div>
          </div>
        )}
      </div>
      {showLegend && (
        <div className="h-14 mt-1 flex items-center justify-center gap-6 text-xs text-gray-600 text-center">
          {!multiSeries && typeof currentLegend === 'string' && (
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
              <span>{currentLegend}</span>
            </div>
          )}
          {!multiSeries && basePreviousData.length > 0 && typeof previousLegend === 'string' && (
            <div className="flex items-center gap-2">
              <span className="inline-block w-5 h-0 align-middle border-t-2" style={{ borderTopColor: previousColor, borderTopStyle: 'dashed' }} />
              <span>{previousLegend}</span>
            </div>
          )}
          {multiSeries && platformIds.length > 0 && (
            platformIds.map((pid) => (
              <button
                key={`legend-${pid}`}
                type="button"
                onClick={() => toggleSeries(pid)}
                className={`flex items-center gap-2 cursor-pointer transition-opacity ${hiddenSeries[pid] ? 'opacity-40' : 'opacity-100'}`}
              >
                <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: platformColors?.[pid] || '#6366f1' }} />
                <span className="capitalize">{pid}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default TimeSeriesChart;


